import { useEffect, useState } from 'react'
import type { Order } from '../../types'
import { fetchOrderById } from '../../services/orders/supabaseOrders'
import { formatTRY } from '../../utils/money'
import { displayTable } from '../../utils/tableLabel'
import { Spinner } from '../Spinner'
import { XIcon } from '../icons'
import './ConfirmDialog.css'
import './OrderInfoDialog.css'

const when = (t: number) =>
  new Date(t).toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })

/**
 * Bir siparişin içeriği, salt okunur (3 Kas 2026, kullanıcı): cariye yazılan
 * müşteri "ne yemiştim" diye sorunca ne yediği ve ne tuttuğu gösterilsin.
 */
export function OrderInfoDialog({
  tenantId,
  orderId,
  onClose,
}: {
  tenantId: string
  orderId: string
  onClose: () => void
}) {
  const [order, setOrder] = useState<Order | null | undefined>(undefined)
  const [error, setError] = useState(false)

  useEffect(() => {
    let live = true
    fetchOrderById(tenantId, orderId)
      .then((o) => live && setOrder(o))
      .catch(() => live && setError(true))
    return () => {
      live = false
    }
  }, [tenantId, orderId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const discount = order ? (order.subtotalKurus ?? order.totalKurus) - order.totalKurus : 0

  return (
    <div
      className="dlg"
      role="dialog"
      aria-modal="true"
      aria-label="Sipariş"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dlg__card dlg__card--x oinfo">
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat">
          <XIcon />
        </button>
        <h2>Ne yedi</h2>

        {error ? (
          <p className="oinfo__empty">Sipariş yüklenemedi. İnternet bağlantınızı kontrol edin.</p>
        ) : order === undefined ? (
          <div className="oinfo__wait">
            <Spinner />
          </div>
        ) : order === null ? (
          <p className="oinfo__empty">Bu siparişin kaydı bulunamadı.</p>
        ) : (
          <>
            <p className="oinfo__meta">
              {when(order.createdAtLocal)}, {displayTable(order.tableName) || 'Gel-Al'}
              {order.receiptNo ? `, fiş ${order.receiptNo}` : ''}
            </p>
            <ul className="oinfo__lines">
              {order.lines.map((l, i) => (
                <li key={i}>
                  <span className="oinfo__qty">{l.qty}×</span>
                  <span className="oinfo__name">
                    {l.name}
                    {l.note && <small>{l.note}</small>}
                  </span>
                  <span className="oinfo__sum">{formatTRY(l.lineTotalKurus)}</span>
                </li>
              ))}
            </ul>
            <div className="oinfo__total">
              {discount > 0 && (
                <div className="oinfo__row">
                  <span>İndirim</span>
                  <span>−{formatTRY(discount)}</span>
                </div>
              )}
              <div className="oinfo__row oinfo__row--total">
                <span>Toplam</span>
                <b>{formatTRY(order.totalKurus)}</b>
              </div>
            </div>
          </>
        )}

        <div className="dlg__buttons">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Tamam
          </button>
        </div>
      </div>
    </div>
  )
}
